// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import VideoPlayer, { VideoPlayerStyles } from "@west/App/Components/ProductVideoPlayerDetails/VideoPlayer";
import { CustomAppState } from "@west/App/Store/Reducers";
import Img, { ImgProps } from "@west/MobiusOverrides/Img/Img";
import { PLP_GRID_VIEW_DOCUMENT_TYPE } from "@west/XDL/constants";
import Pause from "@west/XDL/Icons/Pause";
import PlayCircle from "@west/XDL/Icons/PlayCircle";
import { mergeWithCss } from "@west/XDL/Utilities";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";

interface DocumentObject {
    DocumentType?: string;
    FilePath?: string;
}

interface OwnProps {
    extendedStyles?: ProductListImageVideoCardStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState) => {
    const productsDataView = getProductListDataView(state);

    return {
        productsDataView,
    };
};

export interface ProductListImageVideoCardStyles {
    wrapper?: GridItemProps;
    image?: ImgProps;
    video?: VideoPlayerStyles;
    playPauseIcon?: IconPresentationProps;
}

export const productListImageVideoCardStyles: ProductListImageVideoCardStyles = {};

const ProductListImageVideoCard: React.FC<Props> = ({ productsDataView, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(productListImageVideoCardStyles, extendedStyles));
    const [isVideoPlaying, setIsVideoPlaying] = useState(true);

    if (!productsDataView.value || !productsDataView.properties?.documents) {
        return null;
    }

    const documents: DocumentObject[] = JSON.parse(productsDataView.properties.documents);

    return (
        <GridItem {...styles.wrapper}>
            {documents?.map((document, index) =>
                document.DocumentType === PLP_GRID_VIEW_DOCUMENT_TYPE.Image ? (
                    <Img
                        key={`${document.DocumentType}-${index}`}
                        src={document.FilePath}
                        altText={`${document.DocumentType}-${index}`}
                        {...styles.image}
                    />
                ) : (
                    <>
                        <VideoPlayer
                            key={`${document.DocumentType}-${index}`}
                            imageUrl={document.FilePath}
                            playing={isVideoPlaying}
                            controls={false}
                            muted
                            loop
                            width="100%"
                            height="100%"
                            extendedStyles={styles.video}
                        />
                        <Icon
                            {...styles.playPauseIcon}
                            src={isVideoPlaying ? Pause : PlayCircle}
                            onClick={() => setIsVideoPlaying(!isVideoPlaying)}
                            {...{ isVideoPlaying }}
                        />
                    </>
                ),
            )}
        </GridItem>
    );
};

export default connect(mapStateToProps)(ProductListImageVideoCard);
